import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, ShoppingCart, Users, FileText, DollarSign, TrendingUp, Settings, Plus, Edit, Trash2, Save, Search, Globe, MapPin, Phone, Clock, MessageSquare, Star, Mail, Check, X, RefreshCw, Send, Upload, ChevronRight, Car, Ruler, Scale, Hash, AlertTriangle, Key, Link2, Copy, Eye, EyeOff, Image, GripVertical, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Megaphone, UserPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import InventoryUpload from "../components/InventoryUpload";
import ProductImageManager from "../components/ProductImageManager";
import BannerImageInput from "../components/BannerImageInput";

const AdminDashboardPage = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reviewFilter, setReviewFilter] = useState("all");
  
  // Site Settings State
  const [siteSettings, setSiteSettings] = useState({
    phone: "781-870-7976",
    address: "72 Chelsea Street Everett MA 02149",
    email: "contact@brendollysautoparts.com",
    support_hours: "Mon-Fri: 8AM-6PM, Sat: 9AM-4PM, Sun: Closed",
    whatsapp: "+16172560105"
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Page Content State
  const [pages, setPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [pageForm, setPageForm] = useState({ page_id: "", title: "", content: "" });
  const [savingPage, setSavingPage] = useState(false);

  // Email Template State
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ subject: "", html_content: "" });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // ERP Integration State
  const [erpApiKeys, setErpApiKeys] = useState([]);
  const [erpKeyDialogOpen, setErpKeyDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);

  // Homepage Banners State
  const [homepageBanners, setHomepageBanners] = useState([]);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: "",
    subtitle: "",
    button_text: "Shop Now",
    link: "",
    image_url: "",
    is_active: true
  });
  const [savingBanner, setSavingBanner] = useState(false);

  // Marketing Email State
  const [marketingTemplates, setMarketingTemplates] = useState([]);
  const [subscribers, setSubscribers] = useState({ subscribers: [], total: 0, active_count: 0 });
  const [marketingDialogOpen, setMarketingDialogOpen] = useState(false);
  const [editingMarketing, setEditingMarketing] = useState(null);
  const [marketingForm, setMarketingForm] = useState({
    name: "",
    subject: "",
    html_content: ""
  });
  const [savingMarketing, setSavingMarketing] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");

  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "android-carplay",
    subcategory: "",
    brand: "",
    sku: "",
    stock: "",
    images: [],
    featured: false,
    discount_percent: "0",
    product_type: "parts",  // "parts" or "consumable"
    // Auto parts specific fields
    part_number: "",
    position: "",
    side: "",
    low_stock_threshold: "10",
    weight: "",
    dimensions: { length: "", width: "", height: "" },
    interchange_numbers: "",
    cost_of_goods: "",
    // Vehicle fitment
    compatible_vehicles: []
  });

  // Category definitions with type (parts = requires fitment, consumable = no fitment)
  const categoryConfig = {
    // PRIMARY STORE CATEGORIES
    "android-carplay": { label: "Android CarPlay", type: "consumable", group: "Android CarPlay" },
    "accessories": { label: "Accessories", type: "consumable", group: "Accessories" },
    "led-headlight-bulbs": { label: "LED Headlight Bulbs", type: "consumable", group: "LED Headlight Bulbs" },
    
    // AUTOMOTIVE PARTS (require fitment)
    "brake-pads": { label: "Brake Pads", type: "parts", group: "Brakes" },
    "brake-rotors": { label: "Brake Rotors", type: "parts", group: "Brakes" },
    "brake-calipers": { label: "Brake Calipers", type: "parts", group: "Brakes" },
    "suspension-parts": { label: "Suspension Parts", type: "parts", group: "Suspension" },
    "shocks-struts": { label: "Shocks & Struts", type: "parts", group: "Suspension" },
    "control-arms": { label: "Control Arms", type: "parts", group: "Suspension" },
    "steering-parts": { label: "Steering Parts", type: "parts", group: "Steering" },
    "engine-parts": { label: "Engine Parts", type: "parts", group: "Engine" },
    "exhaust-parts": { label: "Exhaust Parts", type: "parts", group: "Exhaust" },
    "transmission-parts": { label: "Transmission Parts", type: "parts", group: "Transmission" },
    "cooling-parts": { label: "Cooling Parts", type: "parts", group: "Cooling" },
    "electrical-parts": { label: "Electrical Parts", type: "parts", group: "Electrical" },
    "lighting-parts": { label: "Lighting Parts", type: "parts", group: "Lighting" },
    "ignition-parts": { label: "Ignition Parts", type: "parts", group: "Ignition" },
    "other-parts": { label: "Other Parts", type: "parts", group: "Other" },
    
    // CONSUMABLES & UNIVERSAL (no fitment required)
    "brake-cleaner": { label: "Brake Cleaner", type: "consumable", group: "Chemicals" },
    "engine-oil": { label: "Engine Oil", type: "consumable", group: "Fluids" },
    "oil-filters": { label: "Oil Filters", type: "consumable", group: "Filters" },
    "air-filters": { label: "Air Filters", type: "consumable", group: "Filters" },
    "fuel-filters": { label: "Fuel Filters", type: "consumable", group: "Filters" },
    "coolant": { label: "Coolant / Antifreeze", type: "consumable", group: "Fluids" },
    "transmission-fluid": { label: "Transmission Fluid", type: "consumable", group: "Fluids" },
    "brake-fluid": { label: "Brake Fluid", type: "consumable", group: "Fluids" },
    "power-steering-fluid": { label: "Power Steering Fluid", type: "consumable", group: "Fluids" },
    "additives": { label: "Additives & Treatments", type: "consumable", group: "Chemicals" },
    "lubricants": { label: "Lubricants & Greases", type: "consumable", group: "Chemicals" },
    "tools": { label: "Tools", type: "consumable", group: "Tools & Equipment" },
    "car-care": { label: "Car Care Products", type: "consumable", group: "Car Care" },
    "other-consumables": { label: "Other Consumables", type: "consumable", group: "Other" }
  };

  // Check if current category requires fitment
  const requiresFitment = () => {
    const config = categoryConfig[productForm.category];
    return config?.type === "parts";
  };

  // Group categories for display
  const getCategoriesByGroup = () => {
    const groups = {};
    Object.entries(categoryConfig).forEach(([key, value]) => {
      const groupKey = `${value.type === "parts" ? "1-" : "2-"}${value.group}`;
      if (!groups[groupKey]) {
        groups[groupKey] = { label: value.group, type: value.type, items: [] };
      }
      groups[groupKey].items.push({ key, ...value });
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const positions = ["Front", "Rear", "Front & Rear"];
  const sides = ["LH (Left)", "RH (Right)", "Both", "Center"];

  // Vehicle fitment state
  const [newFitment, setNewFitment] = useState({
    year_from: "",
    year_to: "",
    make: "",
    model: "",
    engine: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, ordersRes, contactsRes, settingsRes, pagesRes, reviewsRes, templatesRes, erpKeysRes, bannersRes, marketingRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/products?limit=100`).catch(() => ({ data: { products: [] } })),
        axios.get(`${API}/admin/orders`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/contacts`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: siteSettings })),
        axios.get(`${API}/admin/pages`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/reviews`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/email-templates`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/erp/api-keys`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/homepage-banners`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketing-templates`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/subscribers`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).catch(() => ({ data: { subscribers: [], total: 0, active_count: 0 } }))
      ]);
      setProducts(productsRes.data.products || []);
      const ordersData = ordersRes.data;
      setOrders(Array.isArray(ordersData) ? ordersData : (ordersData?.orders || []));
      setContacts(contactsRes.data || []);
      if (settingsRes.data) setSiteSettings(settingsRes.data);
      setPages(pagesRes.data || []);
      setReviews(reviewsRes.data || []);
      setEmailTemplates(templatesRes.data || []);
      setErpApiKeys(erpKeysRes.data || []);
      setHomepageBanners(bannersRes.data || []);
      setMarketingTemplates(marketingRes.data || []);
      setSubscribers(subscribersRes.data || { subscribers: [], total: 0, active_count: 0 });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Save Site Settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        siteSettings,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Site settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Page Content Functions
  const openPageEditor = (page) => {
    setEditingPage(page);
    setPageForm({
      page_id: page.page_id,
      title: page.title,
      content: page.content
    });
    setPageDialogOpen(true);
  };

  const handleSavePage = async (e) => {
    e.preventDefault();
    setSavingPage(true);
    try {
      await axios.put(
        `${API}/admin/pages/${pageForm.page_id}`,
        pageForm,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Page content saved successfully!");
      setPageDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving page:", error);
      toast.error("Failed to save page content");
    } finally {
      setSavingPage(false);
    }
  };

  // Review Functions
  const handleModerateReview = async (reviewId, status) => {
    try {
      await axios.put(
        `${API}/admin/reviews/${reviewId}`,
        null,
        {
          params: { status },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(`Review ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Review deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const filteredReviews = reviewFilter === "all" 
    ? reviews 
    : reviews.filter(r => r.status === reviewFilter);

  // Email Template Functions
  const openTemplateEditor = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      subject: template.subject,
      html_content: template.html_content
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setSavingTemplate(true);
    try {
      await axios.put(
        `${API}/admin/email-templates/${editingTemplate.template_id}`,
        templateForm,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Email template saved!");
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async (templateId) => {
    if (!window.confirm("Reset this template to default?")) return;
    try {
      await axios.post(
        `${API}/admin/email-templates/${templateId}/reset`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Template reset to default");
      fetchData();
    } catch (error) {
      toast.error("Failed to reset template");
    }
  };

  const handleSendTestEmail = async (templateId) => {
    if (!testEmail) {
      toast.error("Enter an email address");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/email-templates/${templateId}/test`,
        null,
        {
          params: { test_email: testEmail },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Test email sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    }
  };

  // ERP API Key Functions
  const handleCreateErpKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Enter a name for the API key");
      return;
    }
    setCreatingKey(true);
    try {
      const res = await axios.post(
        `${API}/admin/erp/api-keys`,
        { name: newKeyName, permissions: ["inventory:read", "inventory:write", "orders:read", "orders:write", "webhooks:manage"] },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      setNewlyCreatedKey(res.data);
      toast.success("API key created! Copy it now - it won't be shown again.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create API key");
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeErpKey = async (keyId) => {
    if (!window.confirm("Revoke this API key? Any integrations using it will stop working.")) return;
    try {
      await axios.delete(`${API}/admin/erp/api-keys/${keyId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("API key revoked");
      fetchData();
    } catch (error) {
      toast.error("Failed to revoke API key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Homepage Banner Functions
  const resetBannerForm = () => {
    setBannerForm({
      title: "",
      subtitle: "",
      button_text: "Shop Now",
      link: "",
      image_url: "",
      is_active: true
    });
    setEditingBanner(null);
  };

  const openBannerDialog = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setBannerForm({
        title: banner.title,
        subtitle: banner.subtitle || "",
        button_text: banner.button_text || "Shop Now",
        link: banner.link,
        image_url: banner.image_url,
        is_active: banner.is_active
      });
    } else {
      resetBannerForm();
    }
    setBannerDialogOpen(true);
  };

  const handleSaveBanner = async () => {
    if (!bannerForm.title || !bannerForm.link || !bannerForm.image_url) {
      toast.error("Title, link, and banner image are required");
      return;
    }
    setSavingBanner(true);
    try {
      if (editingBanner) {
        await axios.put(
          `${API}/admin/homepage-banners/${editingBanner.banner_id}`,
          bannerForm,
          {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Banner updated!");
      } else {
        await axios.post(
          `${API}/admin/homepage-banners`,
          bannerForm,
          {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Banner created!");
      }
      setBannerDialogOpen(false);
      resetBannerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save banner");
    } finally {
      setSavingBanner(false);
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await axios.delete(`${API}/admin/homepage-banners/${bannerId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Banner deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete banner");
    }
  };

  const handleToggleBanner = async (banner) => {
    try {
      await axios.put(
        `${API}/admin/homepage-banners/${banner.banner_id}`,
        { is_active: !banner.is_active },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(banner.is_active ? "Banner disabled" : "Banner enabled");
      fetchData();
    } catch (error) {
      toast.error("Failed to update banner");
    }
  };

  const moveBanner = async (index, direction) => {
    const newBanners = [...homepageBanners];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newBanners.length) return;
    
    // Swap
    [newBanners[index], newBanners[targetIndex]] = [newBanners[targetIndex], newBanners[index]];
    
    // Get new order of IDs
    const bannerIds = newBanners.map(b => b.banner_id);
    
    try {
      await axios.put(
        `${API}/admin/homepage-banners/reorder`,
        bannerIds,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      setHomepageBanners(newBanners);
      toast.success("Banner order updated");
    } catch (error) {
      toast.error("Failed to reorder banners");
    }
  };

  const seedDefaultBanners = async () => {
    try {
      const res = await axios.post(
        `${API}/admin/homepage-banners/seed`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to seed banners");
    }
  };

  // Marketing Email Functions
  const resetMarketingForm = () => {
    setMarketingForm({ name: "", subject: "", html_content: "" });
    setEditingMarketing(null);
  };

  const openMarketingDialog = (template = null) => {
    if (template) {
      setEditingMarketing(template);
      setMarketingForm({
        name: template.name,
        subject: template.subject,
        html_content: template.html_content
      });
    } else {
      resetMarketingForm();
    }
    setMarketingDialogOpen(true);
  };

  const handleSaveMarketing = async () => {
    if (!marketingForm.name || !marketingForm.subject || !marketingForm.html_content) {
      toast.error("All fields are required");
      return;
    }
    setSavingMarketing(true);
    try {
      if (editingMarketing) {
        await axios.put(
          `${API}/admin/marketing-templates/${editingMarketing.template_id}`,
          marketingForm,
          {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Template updated!");
      } else {
        await axios.post(
          `${API}/admin/marketing-templates`,
          null,
          {
            params: marketingForm,
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Template created!");
      }
      setMarketingDialogOpen(false);
      resetMarketingForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingMarketing(false);
    }
  };

  const handleDeleteMarketing = async (templateId) => {
    if (!window.confirm("Delete this email template?")) return;
    try {
      await axios.delete(`${API}/admin/marketing-templates/${templateId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleSendMarketingTest = async (templateId) => {
    if (!testEmailAddress) {
      toast.error("Enter a test email address");
      return;
    }
    setSendingCampaign(true);
    try {
      await axios.post(
        `${API}/admin/marketing-templates/${templateId}/send`,
        { test_email: testEmailAddress },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(`Test email sent to ${testEmailAddress}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingCampaign(false);
    }
  };

  const handleSendCampaign = async (templateId, templateName) => {
    if (!window.confirm(`Send this email to ${subscribers.active_count} subscribers?`)) return;
    setSendingCampaign(true);
    try {
      const res = await axios.post(
        `${API}/admin/marketing-templates/${templateId}/send`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(`Campaign sent! ${res.data.sent_count} emails delivered.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send campaign");
    } finally {
      setSendingCampaign(false);
    }
  };

  const seedMarketingTemplates = async () => {
    try {
      const res = await axios.post(
        `${API}/admin/marketing-templates/seed`,
        {},
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to seed templates");
    }
  };

  // Product Functions
  const resetForm = () => {
    setProductForm({
      name: "",
      description: "",
      price: "",
      category: "android-carplay",
      subcategory: "",
      brand: "",
      sku: "",
      stock: "",
      images: [],
      featured: false,
      discount_percent: "0",
      product_type: "parts",
      position: "",
      side: "",
      low_stock_threshold: "10",
      weight: "",
      dimensions: { length: "", width: "", height: "" },
      interchange_numbers: "",
      cost_of_goods: "",
      compatible_vehicles: []
    });
    setNewFitment({ year_from: "", year_to: "", make: "", model: "", engine: "" });
    setEditingProduct(null);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      subcategory: product.subcategory || "",
      brand: product.brand,
      sku: product.sku,
      stock: product.stock.toString(),
      images: product.images || [],
      featured: product.featured,
      discount_percent: product.discount_percent.toString(),
      product_type: product.product_type || (categoryConfig[product.category]?.type === "parts" ? "parts" : "consumable"),
      position: product.position || "",
      side: product.side || "",
      low_stock_threshold: (product.low_stock_threshold || 10).toString(),
      weight: product.weight ? product.weight.toString() : "",
      dimensions: product.dimensions || { length: "", width: "", height: "" },
      interchange_numbers: (product.interchange_numbers || []).join(", "),
      cost_of_goods: product.cost_of_goods ? product.cost_of_goods.toString() : "",
      compatible_vehicles: product.compatible_vehicles || []
    });
    setDialogOpen(true);
  };

  const addFitment = () => {
    if (!newFitment.make || !newFitment.model) {
      toast.error("Make and Model are required for fitment");
      return;
    }
    const fitment = {
      year_from: newFitment.year_from ? parseInt(newFitment.year_from) : null,
      year_to: newFitment.year_to ? parseInt(newFitment.year_to) : null,
      make: newFitment.make,
      model: newFitment.model,
      engine: newFitment.engine || null
    };
    setProductForm(prev => ({
      ...prev,
      compatible_vehicles: [...prev.compatible_vehicles, fitment]
    }));
    setNewFitment({ year_from: "", year_to: "", make: "", model: "", engine: "" });
  };

  const removeFitment = (index) => {
    setProductForm(prev => ({
      ...prev,
      compatible_vehicles: prev.compatible_vehicles.filter((_, i) => i !== index)
    }));
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Determine product_type based on category
    const productType = categoryConfig[productForm.category]?.type === "parts" ? "parts" : "consumable";
    
    const productData = {
      name: productForm.name,
      description: productForm.description,
      price: parseFloat(productForm.price),
      category: productForm.category,
      subcategory: productForm.subcategory || null,
      brand: productForm.brand,
      sku: productForm.sku,
      stock: parseInt(productForm.stock),
      images: productForm.images.filter(Boolean),
      specifications: {},
      product_type: productType,
      // Only include fitment data for "parts" type
      compatible_vehicles: productType === "parts" ? productForm.compatible_vehicles : [],
      featured: productForm.featured,
      discount_percent: parseFloat(productForm.discount_percent),
      position: productType === "parts" ? (productForm.position || null) : null,
      side: productType === "parts" ? (productForm.side || null) : null,
      low_stock_threshold: parseInt(productForm.low_stock_threshold) || 10,
      weight: productForm.weight ? parseFloat(productForm.weight) : null,
      dimensions: (productForm.dimensions?.length || productForm.dimensions?.width || productForm.dimensions?.height) ? {
        length: parseFloat(productForm.dimensions.length) || 0,
        width: parseFloat(productForm.dimensions.width) || 0,
        height: parseFloat(productForm.dimensions.height) || 0
      } : null,
      interchange_numbers: productForm.interchange_numbers.split(",").map(s => s.trim()).filter(Boolean),
      cost_of_goods: productForm.cost_of_goods ? parseFloat(productForm.cost_of_goods) : null
    };

    try {
      if (editingProduct) {
        await axios.put(
          `${API}/admin/products/${editingProduct.product_id}`,
          productData,
          {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Product updated successfully!");
      } else {
        await axios.post(
          `${API}/admin/products`,
          productData,
          {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        toast.success("Product created successfully!");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Product deleted successfully!");
      fetchData();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Order status updated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalRevenue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
  const lowStockProducts = products.filter(p => p.stock < 10);
  const pendingReviews = reviews.filter(r => r.status === "pending").length;

  const pageNames = {
    about: "About Us",
    returns: "Returns & Refunds",
    shipping: "Shipping Policy",
    terms: "Terms of Service",
    privacy: "Privacy Policy"
  };

  return (
    <div className="min-h-screen bg-gray-50 animate-fade-in" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-primary font-medium uppercase tracking-wider text-sm">Admin Panel</span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 uppercase tracking-tight mt-1" data-testid="page-title">
                Dashboard
              </h1>
            </div>
            <Link to="/admin/blog">
              <Button variant="outline" className="border-gray-300">
                <FileText className="w-4 h-4 mr-2" />
                Manage Blog
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {/* Products Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-primary/30 transition-all cursor-default">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{products.length}</p>
                <p className="text-gray-500 text-sm">Products</p>
              </div>
            </div>
          </div>
          
          {/* Orders Card - Clickable */}
          <Link
            to="/admin/orders"
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-secondary/50 transition-all group cursor-pointer"
            data-testid="orders-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-secondary/10 rounded-lg group-hover:bg-secondary/20 transition-colors">
                  <ShoppingCart className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
                  <p className="text-gray-500 text-sm">Orders</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
          
          {/* Revenue Card - Clickable */}
          <Link
            to="/admin/revenue"
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-green-400/50 transition-all group cursor-pointer"
            data-testid="revenue-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">${totalRevenue.toFixed(2)}</p>
                  <p className="text-gray-500 text-sm">Revenue</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
          
          {/* Low Stock Card - Clickable */}
          <Link
            to="/admin/low-stock"
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-amber-400/50 transition-all group cursor-pointer"
            data-testid="low-stock-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition-colors">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{lowStockProducts.length}</p>
                  <p className="text-gray-500 text-sm">Low Stock</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
          
          {/* Pending Reviews Card - Clickable */}
          <Link
            to="/admin/reviews"
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-purple-400/50 transition-all group cursor-pointer"
            data-testid="reviews-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                  <Star className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{pendingReviews}</p>
                  <p className="text-gray-500 text-sm">Pending Reviews</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
        </div>

        {/* Quick Access: Bulk Shipping Editor */}
        <Link
          to="/admin/shipping-editor"
          className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-blue-400/50 transition-all group"
          data-testid="shipping-editor-link"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Bulk Shipping Editor</p>
              <p className="text-xs text-gray-500">Quickly manage product weights &amp; dimensions for accurate UPS rates</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
        </Link>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-white border border-gray-200 p-1 mb-6 flex-wrap h-auto gap-1">
            <TabsTrigger value="products" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-primary data-[state=active]:text-white relative">
              <Star className="w-4 h-4 mr-2" />
              Reviews
              {pendingReviews > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{pendingReviews}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" />
              Email Templates
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" />
              Site Settings
            </TabsTrigger>
            <TabsTrigger value="pages" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Globe className="w-4 h-4 mr-2" />
              Page Content
            </TabsTrigger>
            <TabsTrigger value="inventory" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Upload className="w-4 h-4 mr-2" />
              Inventory Upload
            </TabsTrigger>
            <TabsTrigger value="banners" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Image className="w-4 h-4 mr-2" />
              Homepage Banners
            </TabsTrigger>
            <TabsTrigger value="marketing" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Megaphone className="w-4 h-4 mr-2" />
              Marketing Emails
            </TabsTrigger>
            <TabsTrigger value="erp" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <Key className="w-4 h-4 mr-2" />
              ERP Integration
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-gray-200"
                />
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-gray-900 font-heading text-xl uppercase">
                      {editingProduct ? "Edit Product" : "Add New Product"}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleProductSubmit} className="space-y-6 mt-4">
                    {/* Basic Info Section */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Package className="w-4 h-4" /> Basic Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-700">Product Name *</Label>
                          <Input
                            value={productForm.name}
                            onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">SKU *</Label>
                          <Input
                            value={productForm.sku}
                            onChange={(e) => setProductForm({...productForm, sku: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                            required
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <Label className="text-gray-700">Description *</Label>
                        <Textarea
                          value={productForm.description}
                          onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                          className="mt-1 bg-white border-gray-200"
                          rows={3}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <Label className="text-gray-700">Category *</Label>
                          <select
                            value={productForm.category}
                            onChange={(e) => setProductForm({...productForm, category: e.target.value, subcategory: "", position: "", side: "", compatible_vehicles: []})}
                            className="w-full mt-1 bg-white border border-gray-200 text-gray-900 rounded-md px-3 py-2"
                            data-testid="product-category-select"
                          >
                            {getCategoriesByGroup().map(([groupKey, group]) => (
                              <optgroup 
                                key={groupKey} 
                                label={`${group.type === "parts" ? "🔧 " : "🛢️ "}${group.label}`}
                              >
                                {group.items.map((item) => (
                                  <option key={item.key} value={item.key}>{item.label}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <p className="text-xs mt-1">
                            {requiresFitment() ? (
                              <span className="text-blue-600 flex items-center gap-1">
                                <Car className="w-3 h-3" /> Auto Part - Fitment fields shown below
                              </span>
                            ) : (
                              <span className="text-green-600">Universal / Consumable - No fitment required</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-700">Brand *</Label>
                          <Input
                            value={productForm.brand}
                            onChange={(e) => setProductForm({...productForm, brand: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                            required
                            data-testid="product-brand-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Pricing & Stock Section */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" /> Pricing & Inventory
                      </h3>
                      <div className="grid grid-cols-5 gap-4">
                        <div>
                          <Label className="text-gray-700">Price ($) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.price}
                            onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">Cost of Goods ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.cost_of_goods}
                            onChange={(e) => setProductForm({...productForm, cost_of_goods: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                            placeholder="For profit calc"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">Discount %</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={productForm.discount_percent}
                            onChange={(e) => setProductForm({...productForm, discount_percent: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">Stock *</Label>
                          <Input
                            type="number"
                            value={productForm.stock}
                            onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">Low Stock Alert</Label>
                          <Input
                            type="number"
                            value={productForm.low_stock_threshold}
                            onChange={(e) => setProductForm({...productForm, low_stock_threshold: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Position / Side Section (Auto Parts Only) */}
                    {requiresFitment() && (
                      <div className="border border-gray-200 rounded-lg p-4 bg-blue-50/30" data-testid="position-side-section">
                        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                          <Car className="w-4 h-4" /> Position / Side (Auto Parts)
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-700">Position</Label>
                            <select
                              value={productForm.position}
                              onChange={(e) => setProductForm({...productForm, position: e.target.value})}
                              className="w-full mt-1 bg-white border border-gray-200 text-gray-900 rounded-md px-3 py-2"
                              data-testid="product-position-select"
                            >
                              <option value="">Not Applicable</option>
                              {positions.map((pos) => (
                                <option key={pos} value={pos}>{pos}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Front, Rear, or both</p>
                          </div>
                          <div>
                            <Label className="text-gray-700">Side</Label>
                            <select
                              value={productForm.side}
                              onChange={(e) => setProductForm({...productForm, side: e.target.value})}
                              className="w-full mt-1 bg-white border border-gray-200 text-gray-900 rounded-md px-3 py-2"
                              data-testid="product-side-select"
                            >
                              <option value="">Not Applicable</option>
                              {sides.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Left (LH), Right (RH), Both, or Center</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Vehicle Fitment Section (Auto Parts Only) */}
                    {requiresFitment() && (
                      <div className="border border-gray-200 rounded-lg p-4 bg-green-50/30" data-testid="vehicle-fitment-section">
                        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                          <Car className="w-4 h-4" /> Vehicle Fitment
                        </h3>
                        
                        {/* Existing Fitments */}
                        {productForm.compatible_vehicles.length > 0 && (
                          <div className="mb-4 space-y-2">
                            {productForm.compatible_vehicles.map((fit, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white border rounded px-3 py-2">
                                <span className="text-sm">
                                  {fit.year_from && fit.year_to ? `${fit.year_from}-${fit.year_to}` : fit.year_from || fit.year_to || "All Years"}{" "}
                                  <strong>{fit.make}</strong> {fit.model}
                                  {fit.engine && ` (${fit.engine})`}
                                </span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => removeFitment(idx)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add New Fitment */}
                        <div className="grid grid-cols-6 gap-2 items-end">
                          <div>
                            <Label className="text-gray-700 text-xs">Year From</Label>
                            <Input
                              type="number"
                              placeholder="2010"
                              value={newFitment.year_from}
                              onChange={(e) => setNewFitment({...newFitment, year_from: e.target.value})}
                              className="mt-1 bg-white border-gray-200"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 text-xs">Year To</Label>
                            <Input
                              type="number"
                              placeholder="2024"
                              value={newFitment.year_to}
                              onChange={(e) => setNewFitment({...newFitment, year_to: e.target.value})}
                              className="mt-1 bg-white border-gray-200"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 text-xs">Make *</Label>
                            <Input
                              placeholder="Toyota"
                              value={newFitment.make}
                              onChange={(e) => setNewFitment({...newFitment, make: e.target.value})}
                              className="mt-1 bg-white border-gray-200"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 text-xs">Model *</Label>
                            <Input
                              placeholder="Camry"
                              value={newFitment.model}
                              onChange={(e) => setNewFitment({...newFitment, model: e.target.value})}
                              className="mt-1 bg-white border-gray-200"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700 text-xs">Engine</Label>
                            <Input
                              placeholder="2.5L"
                              value={newFitment.engine}
                              onChange={(e) => setNewFitment({...newFitment, engine: e.target.value})}
                              className="mt-1 bg-white border-gray-200"
                            />
                          </div>
                          <div>
                            <Button
                              type="button"
                              onClick={addFitment}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid="add-fitment-btn"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Add multiple vehicle fitments. Make and Model are required.</p>
                      </div>
                    )}

                    {/* Interchange Numbers */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Hash className="w-4 h-4" /> Interchange / Part Numbers
                      </h3>
                      <div>
                        <Label className="text-gray-700">Cross-Reference Numbers</Label>
                        <Input
                          value={productForm.interchange_numbers}
                          onChange={(e) => setProductForm({...productForm, interchange_numbers: e.target.value})}
                          className="mt-1 bg-white border-gray-200"
                          placeholder="OEM-123, ALT-456, XREF-789 (comma-separated)"
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter OEM and aftermarket part numbers that this product replaces</p>
                      </div>
                    </div>

                    {/* Shipping / Dimensions Section */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Ruler className="w-4 h-4" /> Shipping Specs
                      </h3>
                      <p className="text-xs text-gray-500 mb-3">Used for accurate UPS shipping rate calculation. Units: weight in lbs, dimensions in inches.</p>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label className="text-gray-700">Weight (lbs)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={productForm.weight}
                            onChange={(e) => setProductForm({...productForm, weight: e.target.value})}
                            className="mt-1 bg-white border-gray-200"
                            placeholder="2.5"
                            data-testid="product-weight-input"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">Length (in)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={productForm.dimensions?.length || ""}
                            onChange={(e) => setProductForm({...productForm, dimensions: {...productForm.dimensions, length: e.target.value}})}
                            className="mt-1 bg-white border-gray-200"
                            placeholder="12"
                            data-testid="product-length-input"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">Width (in)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={productForm.dimensions?.width || ""}
                            onChange={(e) => setProductForm({...productForm, dimensions: {...productForm.dimensions, width: e.target.value}})}
                            className="mt-1 bg-white border-gray-200"
                            placeholder="6"
                            data-testid="product-width-input"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700">Height (in)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            value={productForm.dimensions?.height || ""}
                            onChange={(e) => setProductForm({...productForm, dimensions: {...productForm.dimensions, height: e.target.value}})}
                            className="mt-1 bg-white border-gray-200"
                            placeholder="4"
                            data-testid="product-height-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Images Section */}
                    <ProductImageManager
                      images={productForm.images}
                      onImagesChange={(newImages) => setProductForm({...productForm, images: newImages})}
                      token={token}
                    />

                    {/* Featured Toggle */}
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <Switch
                        checked={productForm.featured}
                        onCheckedChange={(checked) => setProductForm({...productForm, featured: checked})}
                      />
                      <div>
                        <Label className="text-gray-700">Featured Product</Label>
                        <p className="text-xs text-gray-500">Display on homepage and promotional areas</p>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="border-gray-200">
                        Cancel
                      </Button>
                      <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90">
                        {submitting ? "Saving..." : editingProduct ? "Update Product" : "Create Product"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white h-20 animate-pulse rounded-lg border border-gray-200"></div>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left text-gray-600 text-sm font-medium px-4 py-3">Product</th>
                        <th className="text-left text-gray-600 text-sm font-medium px-4 py-3">SKU</th>
                        <th className="text-left text-gray-600 text-sm font-medium px-4 py-3">Price</th>
                        <th className="text-left text-gray-600 text-sm font-medium px-4 py-3">Stock</th>
                        <th className="text-left text-gray-600 text-sm font-medium px-4 py-3">Category</th>
                        <th className="text-right text-gray-600 text-sm font-medium px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.product_id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <img
                                src={product.images?.[0] || "https://via.placeholder.com/40"}
                                alt={product.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                              <div>
                                <p className="text-gray-900 font-medium text-sm">{product.name}</p>
                                <p className="text-gray-500 text-xs">{product.brand}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-sm">{product.sku}</td>
                          <td className="px-4 py-3 text-gray-900 text-sm font-medium">${product.price.toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${product.stock < 10 ? 'text-red-600' : 'text-green-600'}`}>
                              {product.stock}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 text-sm capitalize">{product.category}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)} className="text-gray-600 hover:text-primary">
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(product.product_id)} className="text-gray-600 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {orders.length === 0 ? (
              <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
                <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No orders yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.order_id} className="bg-white border border-gray-200 rounded-lg p-6" data-testid={`order-${order.order_id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-gray-900 font-medium">Order #{order.order_number || order.order_id?.slice(0, 12)}</p>
                        <p className="text-gray-500 text-sm">{new Date(order.created_at).toLocaleString()}</p>
                        {order.payment_transaction_id && (
                          <p className="text-gray-400 text-xs mt-1 font-mono">TXN: {order.payment_transaction_id}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-gray-900 font-bold">${order.total?.toFixed(2)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {order.payment_status && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              order.payment_status === 'captured' ? 'bg-green-100 text-green-700' :
                              order.payment_status === 'refunded' ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {order.payment_status}
                            </span>
                          )}
                        </div>
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order.order_id, e.target.value)}
                          className="mt-1 bg-white border border-gray-200 text-gray-900 text-sm rounded px-2 py-1"
                          data-testid={`order-status-select-${order.order_id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {order.items?.map((item, idx) => (
                        <span key={idx}>{item.product_name || item.name} x{item.quantity}{idx < order.items.length - 1 ? ', ' : ''}</span>
                      ))}
                    </div>

                    {/* Tracking Section */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {order.tracking_number ? (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500">Tracking:</span>
                          <span className="font-mono font-medium text-blue-700">{order.tracking_number}</span>
                          <span className="text-gray-400">({order.shipping_carrier || 'UPS'})</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Enter tracking number"
                            className="h-8 text-sm flex-1"
                            data-testid={`tracking-input-${order.order_id}`}
                            onKeyDown={async (e) => {
                              if (e.key !== 'Enter' || !e.target.value.trim()) return;
                              try {
                                await axios.put(`${API}/admin/orders/${order.order_id}/tracking`, {
                                  tracking_number: e.target.value.trim(),
                                  carrier: 'UPS',
                                }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                                toast.success('Tracking added & shipment email sent');
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to add tracking');
                              }
                            }}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            data-testid={`tracking-submit-${order.order_id}`}
                            onClick={async (e) => {
                              const input = e.target.closest('div').querySelector('input');
                              if (!input?.value.trim()) return;
                              try {
                                await axios.put(`${API}/admin/orders/${order.order_id}/tracking`, {
                                  tracking_number: input.value.trim(),
                                  carrier: 'UPS',
                                }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                                toast.success('Tracking added & shipment email sent');
                                fetchData();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to add tracking');
                              }
                            }}
                          >
                            Add Tracking
                          </Button>
                        </div>
                      )}
                    </div>

                    {order.payment_transaction_id && order.payment_status !== 'refunded' && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          data-testid={`refund-btn-${order.order_id}`}
                          onClick={async () => {
                            if (!window.confirm(`Refund $${order.total?.toFixed(2)} for order #${order.order_number || order.order_id?.slice(0, 12)}?`)) return;
                            try {
                              await axios.post(`${API}/admin/orders/${order.order_id}/refund`, {}, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                              });
                              toast.success('Refund processed successfully');
                              fetchData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Refund failed');
                            }
                          }}
                        >
                          Process Refund
                        </Button>
                      </div>
                    )}
                    {order.payment_status === 'refunded' && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-sm text-orange-600 font-medium">Refunded</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <div className="mb-6 flex gap-2">
              {["all", "pending", "approved", "rejected"].map((filter) => (
                <Button
                  key={filter}
                  variant={reviewFilter === filter ? "default" : "outline"}
                  size="sm"
                  onClick={() => setReviewFilter(filter)}
                  className={reviewFilter === filter ? "bg-primary" : "border-gray-200"}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  {filter === "pending" && pendingReviews > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{pendingReviews}</span>
                  )}
                </Button>
              ))}
            </div>

            {filteredReviews.length === 0 ? (
              <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No reviews to display</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReviews.map((review) => (
                  <div key={review.review_id} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {review.product_image && (
                            <img src={review.product_image} alt="" className="w-12 h-12 object-cover rounded" />
                          )}
                          <div>
                            <p className="text-gray-900 font-medium">{review.product_name}</p>
                            <div className="flex items-center gap-1">
                              {[1,2,3,4,5].map((s) => (
                                <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`} />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-900 font-medium">{review.title}</p>
                        <p className="text-gray-600 mt-1">{review.comment}</p>
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                          <span>By: {review.user_name}</span>
                          <span>•</span>
                          <span>{new Date(review.created_at).toLocaleDateString()}</span>
                          <span>•</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            review.status === 'approved' ? 'bg-green-100 text-green-700' :
                            review.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>{review.status}</span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {review.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => handleModerateReview(review.review_id, "approved")} className="bg-green-600 hover:bg-green-700">
                              <Check className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleModerateReview(review.review_id, "rejected")} className="border-red-300 text-red-600 hover:bg-red-50">
                              <X className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteReview(review.review_id)} className="text-gray-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="emails">
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-gray-900 uppercase">Email Notification Templates</h3>
                  <p className="text-gray-500 text-sm">Customize the emails sent to customers for order updates</p>
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <strong>Note:</strong> Email sending requires a Resend API key. Add your RESEND_API_KEY to the backend .env file to enable email notifications.
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {emailTemplates.map((template) => (
                <div key={template.template_id} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{template.name}</h4>
                      <p className="text-gray-500 text-sm">{template.description}</p>
                    </div>
                    <Mail className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mb-4 truncate">Subject: {template.subject}</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => openTemplateEditor(template)} className="bg-primary hover:bg-primary/90">
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleResetTemplate(template.template_id)} className="border-gray-200">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Email Template Editor Dialog */}
            <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
              <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-gray-900 font-heading text-xl uppercase">
                    Edit {editingTemplate?.name}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSaveTemplate} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-gray-700">Subject Line</Label>
                    <Input
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                      className="mt-1 bg-white border-gray-200"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Use {"{{variable}}"} for dynamic content like {"{{order_id}}"}, {"{{customer_name}}"}</p>
                  </div>

                  <div>
                    <Label className="text-gray-700">HTML Content</Label>
                    <Textarea
                      value={templateForm.html_content}
                      onChange={(e) => setTemplateForm({...templateForm, html_content: e.target.value})}
                      className="mt-1 bg-white border-gray-200 font-mono text-sm min-h-[400px]"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available variables: {"{{customer_name}}"}, {"{{order_id}}"}, {"{{order_date}}"}, {"{{order_items}}"}, {"{{subtotal}}"}, {"{{shipping}}"}, {"{{tax}}"}, {"{{total}}"}, {"{{shipping_address}}"}, {"{{tracking_number}}"}, {"{{carrier}}"}
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <Label className="text-gray-700">Send Test Email</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        className="bg-white border-gray-200"
                      />
                      <Button type="button" variant="outline" onClick={() => handleSendTestEmail(editingTemplate?.template_id)} className="border-gray-200">
                        <Send className="w-4 h-4 mr-1" /> Send Test
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)} className="border-gray-200">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={savingTemplate} className="bg-primary hover:bg-primary/90">
                      <Save className="w-4 h-4 mr-2" />
                      {savingTemplate ? "Saving..." : "Save Template"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Site Settings Tab */}
          <TabsContent value="settings">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-heading font-bold text-gray-900 uppercase mb-6 flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                Contact Information & Site Settings
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number
                  </Label>
                  <Input
                    value={siteSettings.phone}
                    onChange={(e) => setSiteSettings({...siteSettings, phone: e.target.value})}
                    className="mt-1 bg-white border-gray-200"
                    placeholder="781-870-7976"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-700 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    WhatsApp Number
                  </Label>
                  <Input
                    value={siteSettings.whatsapp}
                    onChange={(e) => setSiteSettings({...siteSettings, whatsapp: e.target.value})}
                    className="mt-1 bg-white border-gray-200"
                    placeholder="+1 617 256 0105"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Store Address
                  </Label>
                  <Input
                    value={siteSettings.address}
                    onChange={(e) => setSiteSettings({...siteSettings, address: e.target.value})}
                    className="mt-1 bg-white border-gray-200"
                    placeholder="123 Auto Parts Blvd, Boston, MA 02101"
                  />
                </div>

                <div>
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Contact Email
                  </Label>
                  <Input
                    type="email"
                    value={siteSettings.email}
                    onChange={(e) => setSiteSettings({...siteSettings, email: e.target.value})}
                    className="mt-1 bg-white border-gray-200"
                    placeholder="info@brendollysautoparts.com"
                  />
                </div>

                <div>
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Support Hours
                  </Label>
                  <Input
                    value={siteSettings.support_hours}
                    onChange={(e) => setSiteSettings({...siteSettings, support_hours: e.target.value})}
                    className="mt-1 bg-white border-gray-200"
                    placeholder="Mon-Fri: 8AM-6PM, Sat: 9AM-4PM"
                  />
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <Button 
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingSettings ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Page Content Tab */}
          <TabsContent value="pages">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-heading font-bold text-gray-900 uppercase mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Edit Page Content
              </h3>
              
              <p className="text-gray-600 mb-6">
                Click on any page below to edit its content. Changes will be reflected on the public-facing pages.
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pages.map((page) => (
                  <div 
                    key={page.page_id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
                    onClick={() => openPageEditor(page)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{pageNames[page.page_id] || page.title}</h4>
                        <p className="text-gray-500 text-sm mt-1 line-clamp-2">{page.content.substring(0, 100)}...</p>
                      </div>
                      <Edit className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Page Editor Dialog */}
              <Dialog open={pageDialogOpen} onOpenChange={setPageDialogOpen}>
                <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-gray-900 font-heading text-xl uppercase">
                      Edit {pageNames[editingPage?.page_id] || editingPage?.title}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSavePage} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-gray-700">Page Title</Label>
                      <Input
                        value={pageForm.title}
                        onChange={(e) => setPageForm({...pageForm, title: e.target.value})}
                        className="mt-1 bg-white border-gray-200"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-gray-700">Content</Label>
                      <Textarea
                        value={pageForm.content}
                        onChange={(e) => setPageForm({...pageForm, content: e.target.value})}
                        className="mt-1 bg-white border-gray-200 min-h-[300px] font-mono text-sm"
                        required
                      />
                      <p className="text-gray-500 text-xs mt-1">You can use basic formatting. For HTML pages, standard HTML tags are supported.</p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setPageDialogOpen(false)} className="border-gray-200">
                        Cancel
                      </Button>
                      <Button type="submit" disabled={savingPage} className="bg-primary hover:bg-primary/90">
                        <Save className="w-4 h-4 mr-2" />
                        {savingPage ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* Inventory Upload Tab */}
          <TabsContent value="inventory">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <InventoryUpload />
            </div>
          </TabsContent>

          {/* Homepage Banners Tab */}
          <TabsContent value="banners">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Image className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-gray-900 uppercase">Homepage Category Banners</h3>
                    <p className="text-gray-500 text-sm">Manage the category banners displayed on the homepage</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {homepageBanners.length === 0 && (
                    <Button variant="outline" onClick={seedDefaultBanners} className="border-gray-200">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Load Defaults
                    </Button>
                  )}
                  <Dialog open={bannerDialogOpen} onOpenChange={(open) => {
                    setBannerDialogOpen(open);
                    if (!open) resetBannerForm();
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary hover:bg-primary/90" onClick={() => openBannerDialog()}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Banner
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-gray-900 font-heading text-xl uppercase">
                          {editingBanner ? "Edit Banner" : "Add New Banner"}
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-700">Title *</Label>
                            <Input
                              value={bannerForm.title}
                              onChange={(e) => setBannerForm({...bannerForm, title: e.target.value})}
                              placeholder="e.g., Brakes"
                              className="mt-1 bg-white border-gray-200"
                            />
                          </div>
                          <div>
                            <Label className="text-gray-700">Button Text</Label>
                            <Input
                              value={bannerForm.button_text}
                              onChange={(e) => setBannerForm({...bannerForm, button_text: e.target.value})}
                              placeholder="Shop Now"
                              className="mt-1 bg-white border-gray-200"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-gray-700">Subtitle</Label>
                          <Input
                            value={bannerForm.subtitle}
                            onChange={(e) => setBannerForm({...bannerForm, subtitle: e.target.value})}
                            placeholder="e.g., High-performance stopping power"
                            className="mt-1 bg-white border-gray-200"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-gray-700">Destination Link *</Label>
                          <Input
                            value={bannerForm.link}
                            onChange={(e) => setBannerForm({...bannerForm, link: e.target.value})}
                            placeholder="/shop/brakes"
                            className="mt-1 bg-white border-gray-200"
                          />
                          <p className="text-xs text-gray-500 mt-1">Use relative paths like /shop/brakes or full URLs</p>
                        </div>
                        
                        <BannerImageInput
                          imageUrl={bannerForm.image_url}
                          onImageChange={(url) => setBannerForm({...bannerForm, image_url: url})}
                          token={token}
                          title={bannerForm.title}
                          subtitle={bannerForm.subtitle}
                        />
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={bannerForm.is_active}
                            onCheckedChange={(checked) => setBannerForm({...bannerForm, is_active: checked})}
                          />
                          <Label className="text-gray-700">Banner Active</Label>
                        </div>
                        
                        <div className="flex justify-end gap-3 pt-4">
                          <Button variant="outline" onClick={() => setBannerDialogOpen(false)} className="border-gray-200">
                            Cancel
                          </Button>
                          <Button onClick={handleSaveBanner} disabled={savingBanner} className="bg-primary hover:bg-primary/90">
                            {savingBanner ? "Saving..." : (editingBanner ? "Update Banner" : "Create Banner")}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Banner List */}
              {homepageBanners.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
                  <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-2">No banners configured</p>
                  <p className="text-gray-400 text-sm">Add banners to display on the homepage category section</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {homepageBanners.map((banner, index) => (
                    <div 
                      key={banner.banner_id} 
                      className={`flex items-center gap-4 p-4 border rounded-lg transition-colors ${banner.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'}`}
                    >
                      {/* Reorder Controls */}
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => moveBanner(index, "up")}
                          disabled={index === 0}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => moveBanner(index, "down")}
                          disabled={index === homepageBanners.length - 1}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Thumbnail */}
                      <div className="w-32 h-18 aspect-[16/9] rounded overflow-hidden bg-gray-100 flex-shrink-0">
                        <img 
                          src={banner.image_url} 
                          alt={banner.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 uppercase">{banner.title}</h4>
                        <p className="text-sm text-gray-500 truncate">{banner.subtitle || "No subtitle"}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Link: {banner.link} • Button: {banner.button_text}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleBanner(banner)}
                          className={banner.is_active ? "text-green-600" : "text-gray-400"}
                          title={banner.is_active ? "Disable banner" : "Enable banner"}
                        >
                          {banner.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openBannerDialog(banner)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBanner(banner.banner_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Help Text */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Tips for Great Banners</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Use high-quality images with 16:9 aspect ratio (e.g., 1280x720, 1920x1080)</li>
                  <li>• Keep titles short and impactful (1-2 words work best)</li>
                  <li>• Use consistent image styles across all banners</li>
                  <li>• Reorder banners to prioritize your most important categories</li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* Marketing Emails Tab */}
          <TabsContent value="marketing">
            <div className="space-y-6">
              {/* Subscribers Overview */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <UserPlus className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-gray-900 uppercase">Email Subscribers</h3>
                      <p className="text-gray-500 text-sm">Customers who opted in at checkout</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-green-600">{subscribers.active_count}</p>
                    <p className="text-sm text-gray-500">Active subscribers</p>
                  </div>
                </div>
                
                {/* Recent subscribers preview */}
                {subscribers.subscribers && subscribers.subscribers.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">Recent Subscribers:</p>
                    <div className="flex flex-wrap gap-2">
                      {subscribers.subscribers.slice(0, 5).map((sub, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                          {sub.email}
                        </span>
                      ))}
                      {subscribers.subscribers.length > 5 && (
                        <span className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-500">
                          +{subscribers.subscribers.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Email Templates Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-gray-900 uppercase">Email Templates</h3>
                      <p className="text-gray-500 text-sm">Create and manage marketing email campaigns</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {marketingTemplates.length === 0 && (
                      <Button variant="outline" onClick={seedMarketingTemplates} className="border-gray-200">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Load Defaults
                      </Button>
                    )}
                    <Dialog open={marketingDialogOpen} onOpenChange={(open) => {
                      setMarketingDialogOpen(open);
                      if (!open) resetMarketingForm();
                    }}>
                      <DialogTrigger asChild>
                        <Button className="bg-primary hover:bg-primary/90" onClick={() => openMarketingDialog()}>
                          <Plus className="w-4 h-4 mr-2" />
                          New Template
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-gray-900 font-heading text-xl uppercase">
                            {editingMarketing ? "Edit Email Template" : "Create Email Template"}
                          </DialogTitle>
                        </DialogHeader>
                        
                        <div className="space-y-4 mt-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-gray-700">Template Name *</Label>
                              <Input
                                value={marketingForm.name}
                                onChange={(e) => setMarketingForm({...marketingForm, name: e.target.value})}
                                placeholder="e.g., welcome, promotion, newsletter"
                                className="mt-1 bg-white border-gray-200"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-700">Email Subject *</Label>
                              <Input
                                value={marketingForm.subject}
                                onChange={(e) => setMarketingForm({...marketingForm, subject: e.target.value})}
                                placeholder="e.g., Welcome to Brendolly's Auto Parts!"
                                className="mt-1 bg-white border-gray-200"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-gray-700">Email Content (HTML) *</Label>
                            <Textarea
                              value={marketingForm.html_content}
                              onChange={(e) => setMarketingForm({...marketingForm, html_content: e.target.value})}
                              placeholder="<html>...</html>"
                              className="mt-1 bg-white border-gray-200 font-mono text-sm h-64"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Use placeholders: {"{{name}}"} for customer name, {"{{email}}"} for email, {"{{unsubscribe_link}}"} for unsubscribe
                            </p>
                          </div>
                          
                          {/* Preview */}
                          {marketingForm.html_content && (
                            <div>
                              <Label className="text-gray-700 mb-2 block">Preview</Label>
                              <div className="border border-gray-200 rounded-lg p-4 bg-white max-h-64 overflow-y-auto">
                                <div dangerouslySetInnerHTML={{ 
                                  __html: marketingForm.html_content
                                    .replace(/\{\{name\}\}/g, 'John Doe')
                                    .replace(/\{\{email\}\}/g, 'john@example.com')
                                    .replace(/\{\{unsubscribe_link\}\}/g, '#')
                                }} />
                              </div>
                            </div>
                          )}
                          
                          <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" onClick={() => setMarketingDialogOpen(false)} className="border-gray-200">
                              Cancel
                            </Button>
                            <Button onClick={handleSaveMarketing} disabled={savingMarketing} className="bg-primary hover:bg-primary/90">
                              {savingMarketing ? "Saving..." : (editingMarketing ? "Update Template" : "Create Template")}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Templates List */}
                {marketingTemplates.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
                    <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No email templates yet</p>
                    <p className="text-gray-400 text-sm">Create templates to send marketing emails to your subscribers</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {marketingTemplates.map((template) => (
                      <div key={template.template_id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded uppercase">
                                {template.name}
                              </span>
                              {template.is_active && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Active</span>
                              )}
                            </div>
                            <h4 className="font-medium text-gray-900">{template.subject}</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              Created: {new Date(template.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openMarketingDialog(template)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMarketing(template.template_id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Send Options */}
                        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-4">
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              type="email"
                              placeholder="test@example.com"
                              value={testEmailAddress}
                              onChange={(e) => setTestEmailAddress(e.target.value)}
                              className="max-w-xs bg-white border-gray-200"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendMarketingTest(template.template_id)}
                              disabled={sendingCampaign}
                              className="border-gray-200"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Test
                            </Button>
                          </div>
                          
                          <Button
                            size="sm"
                            onClick={() => handleSendCampaign(template.template_id, template.name)}
                            disabled={sendingCampaign || subscribers.active_count === 0}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Megaphone className="w-4 h-4 mr-2" />
                            Send to {subscribers.active_count} Subscribers
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* GDPR Compliance Note */}
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  GDPR Compliance
                </h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• All subscribers have explicitly opted in at checkout</li>
                  <li>• Every email includes an unsubscribe link</li>
                  <li>• Customer data is stored securely and can be deleted on request</li>
                  <li>• Always test emails before sending to all subscribers</li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* ERP Integration Tab */}
          <TabsContent value="erp">
            <div className="space-y-6">
              {/* API Keys Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Key className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-gray-900 uppercase">ERP API Keys</h3>
                      <p className="text-gray-500 text-sm">Manage API keys for ERP system integration</p>
                    </div>
                  </div>
                  <Dialog open={erpKeyDialogOpen} onOpenChange={(open) => {
                    setErpKeyDialogOpen(open);
                    if (!open) {
                      setNewKeyName("");
                      setNewlyCreatedKey(null);
                      setShowKey(false);
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary hover:bg-primary/90">
                        <Plus className="w-4 h-4 mr-2" />
                        Generate API Key
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-gray-900 font-heading text-xl uppercase">
                          {newlyCreatedKey ? "API Key Created" : "Generate ERP API Key"}
                        </DialogTitle>
                      </DialogHeader>
                      
                      {newlyCreatedKey ? (
                        <div className="space-y-4 mt-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-800 font-medium mb-2">Your API key has been created!</p>
                            <p className="text-green-700 text-sm">Copy it now - this is the only time you'll see it.</p>
                          </div>
                          
                          <div>
                            <Label className="text-gray-700">API Key</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                type={showKey ? "text" : "password"}
                                value={newlyCreatedKey.api_key}
                                readOnly
                                className="bg-gray-50 border-gray-200 font-mono text-sm"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setShowKey(!showKey)}
                                className="border-gray-200"
                              >
                                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => copyToClipboard(newlyCreatedKey.api_key)}
                                className="border-gray-200"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="flex justify-end">
                            <Button onClick={() => { setErpKeyDialogOpen(false); setNewlyCreatedKey(null); }}>
                              Done
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label className="text-gray-700">Key Name *</Label>
                            <Input
                              value={newKeyName}
                              onChange={(e) => setNewKeyName(e.target.value)}
                              placeholder="e.g., Main ERP System"
                              className="mt-1 bg-white border-gray-200"
                            />
                            <p className="text-xs text-gray-500 mt-1">A descriptive name to identify this key</p>
                          </div>
                          
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-blue-800 text-sm font-medium mb-2">Permissions Included:</p>
                            <ul className="text-blue-700 text-sm space-y-1">
                              <li>• Read/Write inventory levels</li>
                              <li>• Read/Write orders</li>
                              <li>• Manage webhooks</li>
                            </ul>
                          </div>
                          
                          <div className="flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setErpKeyDialogOpen(false)} className="border-gray-200">
                              Cancel
                            </Button>
                            <Button onClick={handleCreateErpKey} disabled={creatingKey} className="bg-primary hover:bg-primary/90">
                              {creatingKey ? "Creating..." : "Generate Key"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </div>

                {/* API Keys List */}
                {erpApiKeys.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
                    <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No API keys created yet</p>
                    <p className="text-gray-400 text-sm">Generate an API key to connect your ERP system</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {erpApiKeys.map((key) => (
                      <div key={key.key_id} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${key.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                            <Key className={`w-5 h-5 ${key.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{key.name}</p>
                            <p className="text-sm text-gray-500">
                              Key: {key.api_key_preview} • Created: {new Date(key.created_at).toLocaleDateString()}
                              {key.last_used && ` • Last used: ${new Date(key.last_used).toLocaleDateString()}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${key.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {key.is_active ? 'Active' : 'Revoked'}
                          </span>
                          {key.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevokeErpKey(key.key_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* API Documentation Section */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Link2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-gray-900 uppercase">API Endpoints</h3>
                    <p className="text-gray-500 text-sm">Reference for ERP integration</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Authentication</h4>
                    <p className="text-gray-600 text-sm mb-2">Include the API key in the header of all requests:</p>
                    <code className="block bg-gray-900 text-green-400 p-3 rounded text-sm font-mono">
                      X-ERP-API-Key: your_api_key_here
                    </code>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-primary" /> Inventory
                      </h4>
                      <ul className="text-sm space-y-2 text-gray-600">
                        <li><code className="bg-gray-100 px-1 rounded">GET /api/erp/inventory</code> - List all products</li>
                        <li><code className="bg-gray-100 px-1 rounded">GET /api/erp/inventory/{'{sku}'}</code> - Get by SKU</li>
                        <li><code className="bg-gray-100 px-1 rounded">PUT /api/erp/inventory/{'{sku}'}</code> - Update stock</li>
                        <li><code className="bg-gray-100 px-1 rounded">PUT /api/erp/inventory/bulk</code> - Bulk update</li>
                      </ul>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 text-secondary" /> Orders
                      </h4>
                      <ul className="text-sm space-y-2 text-gray-600">
                        <li><code className="bg-gray-100 px-1 rounded">GET /api/erp/orders</code> - List orders</li>
                        <li><code className="bg-gray-100 px-1 rounded">GET /api/erp/orders/{'{id}'}</code> - Order details</li>
                        <li><code className="bg-gray-100 px-1 rounded">PUT /api/erp/orders/{'{id}'}/status</code> - Update status</li>
                        <li><code className="bg-gray-100 px-1 rounded">POST /api/erp/orders/{'{id}'}/tracking</code> - Add tracking</li>
                      </ul>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-green-600" /> Webhooks
                      </h4>
                      <ul className="text-sm space-y-2 text-gray-600">
                        <li><code className="bg-gray-100 px-1 rounded">POST /api/erp/webhooks</code> - Register webhook</li>
                        <li><code className="bg-gray-100 px-1 rounded">GET /api/erp/webhooks</code> - List webhooks</li>
                        <li><code className="bg-gray-100 px-1 rounded">DELETE /api/erp/webhooks/{'{id}'}</code> - Remove</li>
                        <li><code className="bg-gray-100 px-1 rounded">GET /api/erp/webhooks/events</code> - Available events</li>
                      </ul>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" /> Webhook Events
                      </h4>
                      <ul className="text-sm space-y-2 text-gray-600">
                        <li><code className="bg-amber-50 px-1 rounded text-amber-700">inventory.low_stock</code></li>
                        <li><code className="bg-amber-50 px-1 rounded text-amber-700">inventory.out_of_stock</code></li>
                        <li><code className="bg-blue-50 px-1 rounded text-blue-700">order.created</code></li>
                        <li><code className="bg-green-50 px-1 rounded text-green-700">order.shipped</code></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
